import {
  Connections,
  ConnectionsJSON,
  DataObject,
  DataObjectId,
  Diff,
  Entity,
  EntityJSON,
  isDataObjectId
} from 'yougile-model'
import {logger, Util, UUID} from 'yougile-util'
import {Backend} from '../../common/backend'
import {globals} from '../extensions/globals-base'
import {Sync} from './sync'

export class SyncUpdate {
  public fromRemote: boolean
  public companyId: DataObjectId
  public ids: DataObjectId[]
  constructor(
    fromRemote: boolean,
    companyId: DataObjectId,
    ids: DataObjectId[]
  ) {
    this.fromRemote = fromRemote
    this.companyId = companyId
    this.ids = ids
  }
}

export type SyncListenerFilter =
  | DataObjectId
  | DataObjectId[]
  | '*'
  | (() => DataObjectId | DataObjectId[] | undefined)

export interface SyncListener {
  companyId: DataObjectId
  handler: (res: SyncUpdate[]) => void
  filter: SyncListenerFilter
  onlyRemote: boolean
}

export class SyncEvents {
  // todo: reuse gaps
  private static updateListeners: Map<number, SyncListener> = new Map()
  private static anyListeners: Map<UUID, (companyId: DataObjectId) => void> =
    new Map()
  private static newCompanyListeners: Map<
    UUID,
    (companyId: DataObjectId) => void
  > = new Map()
  private static incrementValue = 1

  public static clean(): void {
    SyncEvents.updateListeners.clear()
    SyncEvents.anyListeners.clear()
    SyncEvents.newCompanyListeners.clear()
  }

  public static addNewCompanyListener(
    l: (companyId: DataObjectId) => void
  ): UUID {
    const id = Util.UUID()
    SyncEvents.newCompanyListeners.set(id, l)
    return id
  }

  public static removeNewCompanyListener(id: UUID): void {
    if (!id) {
      return
    }
    if (!SyncEvents.newCompanyListeners.has(id)) {
      logger.warn('new company listener not found', id)
    }
    SyncEvents.newCompanyListeners.delete(id)
  }

  public static addAnyListener(l: (companyId: DataObjectId) => void): UUID {
    const id = Util.UUID()
    SyncEvents.anyListeners.set(id, l)
    return id
  }

  public static removeAnyListener(id: UUID): void {
    if (!id) {
      return
    }
    if (!SyncEvents.anyListeners.has(id)) {
      logger.warn('any listener not found', id)
    }
    SyncEvents.anyListeners.delete(id)
  }

  /**
   * Allows adding listeners for object sync changes events.
   */
  public static addListener(
    companyId: DataObjectId,
    filter: SyncListenerFilter,
    handler: (res: SyncUpdate[]) => void,
    onlyRemote = false
  ): number {
    const l: SyncListener = {companyId, handler, filter, onlyRemote}
    SyncEvents.incrementValue += 1
    const ix = SyncEvents.incrementValue
    if (SyncEvents.updateListeners.has(ix)) {
      throw new Error('already…')
    }
    SyncEvents.updateListeners.set(ix, l)
    return ix
  }

  public static removeListener(id: number): void {
    if (!id) {
      return
    }
    if (!SyncEvents.updateListeners.has(id)) {
      logger.warn('listener not found', id)
      return
    }
    SyncEvents.updateListeners.delete(id)
  }

  public static triggerAll(companyId: DataObjectId): void {
    ;[...SyncEvents.updateListeners.values()].forEach(l => {
      if (l.companyId === companyId) {
        Util.catch(() => l.handler([]))
      }
    })
    ;[...SyncEvents.anyListeners.values()].forEach(l =>
      Util.catch(() => l(companyId))
    )
  }

  public static triggerNewCompany(companyId: DataObjectId): void {
    ;[...SyncEvents.newCompanyListeners.values()].forEach(l =>
      Util.catch(() => l(companyId))
    )
    ;[...SyncEvents.anyListeners.values()].forEach(l =>
      Util.catch(() => l(companyId))
    )
  }

  public static trigger(
    companyId: DataObjectId,
    ids: DataObjectId[],
    fromRemote: boolean
  ): void {
    SyncEvents.triggerUpdate(
      companyId,
      [new SyncUpdate(fromRemote, companyId, ids)],
      fromRemote
    )
  }

  public static triggerUpdate(
    companyId: DataObjectId,
    res: SyncUpdate[],
    fromRemote: boolean
  ): void {
    const go = () => {
      let count = 0
      const entityIds: Set<DataObjectId> = new Set()
      res.forEach((v: SyncUpdate) => {
        v.ids.forEach((id: DataObjectId) => entityIds.add(id))
      })
      new Map(SyncEvents.updateListeners).forEach((l: SyncListener) => {
        if (l?.handler && l.filter && l.companyId === companyId) {
          const filter: DataObjectId | DataObjectId[] | '*' | undefined =
            typeof l.filter === 'function' ? l.filter() : l.filter
          if (filter instanceof Array) {
            const filters: DataObjectId[] = filter
            if (filters.length > 0) {
              if (filters.some((id: DataObjectId) => id && entityIds.has(id))) {
                if (!l.onlyRemote || fromRemote) {
                  count++
                  Util.catch(() => l.handler(res))
                }
              }
            }
          } else {
            if (filter && (filter === '*' || entityIds.has(filter))) {
              if (!l.onlyRemote || fromRemote) {
                count++
                Util.catch(() => l.handler(res))
              }
            }
          }
        }
      })
      ;[...SyncEvents.anyListeners.values()].forEach(l =>
        Util.catch(() => l(companyId))
      )
      if (SyncEvents.updateListeners.size > 1000) {
        logger.log(
          `sync up ${count} / ${SyncEvents.updateListeners.size}`,
          SyncEvents.anyListeners.size
        )
      }
    }
    setTimeout(() => go(), 20)
  }

  /**
   * Depending on what had changed in the given object this method can
   * suggest to update other objects (for example parent Hubs or connected
   * Entities).
   */
  public static updFromObjectAndDiff(
    oldObj: DataObject,
    diff: Diff,
    opts: {
      fromRemote: boolean
      companyId: DataObjectId
    }
  ): SyncUpdate {
    const {fromRemote, companyId} = opts
    if (!oldObj) {
      if (diff.isNew) {
        if (!diff.newObj) {
          throw new TypeError('newObj is not set')
        }
        if (diff.newObj.dataType === 'Connections') {
          const newConn = diff.newObj as ConnectionsJSON
          if (Sync.get({companyId}).has(newConn.from)) {
            return new SyncUpdate(fromRemote, companyId, [newConn.from])
          } else {
            return new SyncUpdate(fromRemote, companyId, [])
          }
        } else {
          return new SyncUpdate(fromRemote, companyId, [
            diff.newObj.id,
            (diff.newObj as EntityJSON).location
          ])
        }
      } else {
        Backend.report('Update on unexpected old object', globals.currentUserId)
        return new SyncUpdate(fromRemote, companyId, [])
      }
    }
    if (oldObj instanceof Entity) {
      const ids = [oldObj.id]
      let onlyChatChanged = true
      diff.plus.forEach((k: string[][]) => {
        if (k[0][0] !== 'chat') {
          onlyChatChanged = false
        }
      })
      diff.minus.forEach((k: string[][]) => {
        if (k[0][0] !== 'chat') {
          onlyChatChanged = false
        }
      })
      if (!onlyChatChanged && oldObj.location) {
        ids.push(oldObj.location)
      }
      return new SyncUpdate(fromRemote, companyId, ids)
    } else if (oldObj instanceof Connections) {
      const res: Set<DataObjectId> = new Set([oldObj.from])
      const from = Sync.get({companyId}).get(oldObj.from)
      if (from && from.location !== companyId) {
        res.add(from.location)
      }

      ;[...(diff.plus ?? []), ...(diff.minus ?? [])].forEach(v => {
        const p = v[0]
        if (p[0] === 'to' && isDataObjectId(p[1])) {
          res.add(p[1])
          return
        }
        if (p[0] === 'modLists' && isDataObjectId(p[1])) {
          res.add(p[1])
          return
        }
      })
      return new SyncUpdate(fromRemote, companyId, [...res])
    } else {
      throw new TypeError('Unsupported DataObject type')
    }
  }
}
